import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [activeFilter, setActiveFilter] = useState('tous');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      title: 'Création de Sites Internet',
      description: 'Des sites web professionnels, responsive et optimisés pour convertir vos visiteurs en clients. Développement web sur-mesure avec webdesign UX/UI moderne.',
      features: [
        'Webdesign sur-mesure et moderne',
        'Site responsive tous appareils',
        'Optimisation SEO technique incluse',
        'Performance et vitesse maximale',
        'Hébergement sécurisé et maintenance'
      ],
      gradient: 'from-teal-50 to-white',
      icon: 'ri-window-line',
      price: 'À partir de 599€'
    },
    {
      title: 'Conception de Logo & Identité Visuelle',
      description: 'Création de logo sur mesure et identité visuelle de marque complète. Charte graphique professionnelle pour une image cohérente et impactante.',
      features: [
        'Conception de logo unique',
        'Charte graphique complète',
        'Palette de couleurs personnalisée',
        'Déclinaisons print & web',
        'Fichiers vectoriels HD fournis'
      ],
      gradient: 'from-purple-50 to-white',
      icon: 'ri-palette-line',
      price: 'À partir de 299€'
    },
    {
      title: 'Refonte de Site Web',
      description: 'Modernisez votre présence digitale avec une refonte de site web professionnel. Amélioration du design, de l\'expérience utilisateur et des performances.',
      features: [
        'Audit complet de l\'existant',
        'Nouveau webdesign moderne',
        'Migration sécurisée des données',
        'Optimisation SEO technique',
        'Formation et support continu'
      ],
      gradient: 'from-orange-50 to-white',
      icon: 'ri-refresh-line',
      price: 'À partir de 799€'
    },
    {
      title: 'Boutique E-commerce Shopify',
      description: 'Création de boutique e-commerce Shopify ou WooCommerce. Solution complète pour vendre en ligne avec paiement sécurisé et gestion simplifiée.',
      features: [
        'Configuration Shopify/WooCommerce',
        'Design responsive optimisé',
        'Paiement sécurisé intégré',
        'Gestion des stocks automatisée',
        'Formation et support continu'
      ],
      gradient: 'from-slate-900 to-slate-800',
      icon: 'ri-shopping-cart-line',
      price: 'À partir de 1299€',
      dark: true
    }
  ];

  const process = [
    {
      number: '01',
      title: 'Découverte',
      description: 'Nous écoutons vos besoins et analysons votre projet pour définir la meilleure stratégie.',
      icon: 'ri-lightbulb-line',
      color: 'from-teal-500 to-cyan-500'
    },
    {
      number: '02',
      title: 'Conception',
      description: 'Notre équipe crée des maquettes sur-mesure qui reflètent parfaitement votre identité.',
      icon: 'ri-palette-line',
      color: 'from-purple-500 to-pink-500'
    },
    {
      number: '03',
      title: 'Développement',
      description: 'Nous donnons vie à votre projet avec des technologies modernes et performantes.',
      icon: 'ri-code-line',
      color: 'from-orange-500 to-red-500'
    },
    {
      number: '04',
      title: 'Lancement',
      description: 'Mise en ligne optimisée et accompagnement pour assurer le succès de votre projet.',
      icon: 'ri-rocket-line',
      color: 'from-green-500 to-emerald-500'
    }
  ];

  const testimonials = [
    {
      text: 'Une équipe exceptionnelle qui a su comprendre nos besoins et créer un site web qui dépasse toutes nos attentes. Le design est magnifique et les performances sont au rendez-vous !',
      author: 'Sophie Martin',
      company: 'Atelier Créatif',
      service: 'Site Web',
      rating: 5
    },
    {
      text: 'Ma carte de visite numérique fait sensation lors de mes rendez-vous clients. Un outil moderne et professionnel qui simplifie vraiment les échanges.',
      author: 'Thomas Dubois',
      company: 'Consultant Indépendant',
      service: 'Carte Digitale',
      rating: 5
    },
    {
      text: 'Grâce à leur expertise SEO, notre visibilité a explosé. Nous recevons maintenant 3 fois plus de demandes qualifiées chaque mois.',
      author: 'Marie Laurent',
      company: 'Studio Design',
      service: 'SEO',
      rating: 5
    },
    {
      text: 'Un service de maintenance irréprochable. Je suis tranquille, mon site est toujours performant et à jour.',
      author: 'Pierre Rousseau',
      company: 'E-commerce Fashion',
      service: 'Maintenance',
      rating: 5
    },
    {
      text: 'Le meilleur investissement pour mon entreprise. Un site qui convertit et un accompagnement de qualité.',
      author: 'Émilie Bernard',
      company: 'Coach Business',
      service: 'Site Web',
      rating: 5
    }
  ];

  const portfolio = [
    {
      title: 'E-commerce Mode',
      category: 'Site Web',
      image: 'https://public.readdy.ai/ai/img_res/a2205261fbe7c449190d133a905184e1.jpg',
      tags: ['E-commerce', 'Design']
    },
    {
      title: 'Cabinet Avocat',
      category: 'Site Web',
      image: 'https://readdy.ai/api/search-image?query=professional%20law%20firm%20website%20design%20with%20clean%20modern%20layout%2C%20minimalist%20white%20background%2C%20elegant%20typography%2C%20corporate%20business%20aesthetic%2C%20trustworthy%20and%20sophisticated%20appearance%2C%20structured%20content%20presentation&width=600&height=400&seq=portfolio2&orientation=landscape',
      tags: ['Corporate', 'SEO']
    },
    {
      title: 'Restaurant Gastronomique',
      category: 'Site Web',
      image: 'https://readdy.ai/api/search-image?query=elegant%20gourmet%20restaurant%20website%20design%20with%20artistic%20food%20photography%20on%20clean%20white%20background%2C%20refined%20minimalist%20layout%2C%20sophisticated%20presentation%2C%20culinary%20excellence%20showcase%2C%20premium%20dining%20aesthetic&width=600&height=400&seq=portfolio3&orientation=landscape',
      tags: ['Restaurant', 'Booking']
    },
    {
      title: 'Carte Architecte',
      category: 'Carte Digitale',
      image: 'https://readdy.ai/api/search-image?query=modern%20digital%20business%20card%20design%20for%20architect%20with%20minimalist%20geometric%20elements%20on%20clean%20white%20background%2C%20professional%20layout%2C%20QR%20code%20integration%2C%20architectural%20aesthetic%2C%20sleek%20and%20contemporary%20style&width=600&height=400&seq=portfolio4&orientation=landscape',
      tags: ['Carte', 'Design']
    },
    {
      title: 'Agence Immobilière',
      category: 'Site Web',
      image: 'https://readdy.ai/api/search-image?query=contemporary%20real%20estate%20agency%20website%20interface%20with%20property%20listings%20on%20clean%20white%20background%2C%20modern%20professional%20design%2C%20organized%20layout%2C%20high-quality%20property%20images%2C%20user-friendly%20navigation&width=600&height=400&seq=portfolio5&orientation=landscape',
      tags: ['Immobilier', 'CRM']
    },
    {
      title: 'Carte Consultant',
      category: 'Carte Digitale',
      image: 'https://readdy.ai/api/search-image?query=sleek%20digital%20business%20card%20for%20business%20consultant%20with%20modern%20minimalist%20design%20on%20white%20background%2C%20professional%20contact%20information%20display%2C%20QR%20code%20feature%2C%20clean%20typography%2C%20corporate%20elegance&width=600&height=400&seq=portfolio6&orientation=landscape',
      tags: ['Carte', 'Pro']
    }
  ];

  const faqData = [
    {
      question: "Combien coûte la création d'un site web avec DigiCreate ?",
      answer: "Nos tarifs pour la création de sites web commencent à partir de 599€. Ce prix inclut un design sur-mesure moderne, un site responsive optimisé pour tous les appareils, l'optimisation SEO de base, et l'hébergement sécurisé. Le prix final dépend de vos besoins spécifiques et des fonctionnalités souhaitées."
    },
    {
      question: "Qu'est-ce qu'une carte de visite numérique et pourquoi en avoir une ?",
      answer: "Une carte de visite numérique est une solution moderne pour partager vos coordonnées professionnelles instantanément via un QR code ou un lien. À partir de 99€, vous bénéficiez d'un design personnalisé unique, de mises à jour en temps réel, et d'une compatibilité avec tous les smartphones. C'est écologique, toujours à jour, et beaucoup plus pratique qu'une carte papier."
    },
    {
      question: "Combien de temps faut-il pour créer un site web ?",
      answer: "Le délai de création d'un site web varie selon la complexité du projet. En moyenne, un site vitrine standard prend entre 2 à 4 semaines, de la phase de découverte au lancement. Notre processus en 4 étapes (Découverte, Conception, Développement, Lancement) garantit un résultat de qualité dans les meilleurs délais."
    },
    {
      question: "Proposez-vous des services de maintenance après la création du site ?",
      answer: "Oui, nous proposons un service de maintenance et support à partir de 49€/mois. Cela inclut un support prioritaire 24/7, des mises à jour régulières, des sauvegardes automatiques, un monitoring permanent, et des corrections rapides en cas de problème. Votre site reste ainsi toujours performant et sécurisé."
    },
    {
      question: "Comment l'optimisation SEO peut-elle aider mon entreprise ?",
      answer: "Notre service d'optimisation SEO à partir de 299€ améliore votre visibilité sur Google et les autres moteurs de recherche. Nous réalisons un audit SEO complet, optimisons les aspects techniques de votre site, créons du contenu optimisé, et assurons un suivi des performances avec des rapports mensuels. Cela vous permet d'attirer plus de clients qualifiés et d'augmenter votre chiffre d'affaires."
    },
    {
      question: "Travaillez-vous avec des entreprises de toutes tailles ?",
      answer: "Absolument ! DigiCreate accompagne aussi bien les entrepreneurs individuels, les PME que les grandes entreprises dans leur transformation digitale. Nos solutions de création de sites web, cartes de visite numériques, et services SEO sont adaptées à chaque type de structure et budget, avec un accompagnement personnalisé."
    }
  ];

  const filteredPortfolio = activeFilter === 'tous' 
    ? portfolio 
    : portfolio.filter(item => item.category === activeFilter);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    
    formData.forEach((value, key) => {
      if (key === 'service') {
        const select = form.querySelector('select[name="service"]') as HTMLSelectElement;
        const selectedOption = select.options[select.selectedIndex];
        data.append(key, selectedOption.text);
      } else {
        data.append(key, value.toString());
      }
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d5p5kp7hv1n1bb3qco70', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString()
      });

      if (response.ok) {
        alert('Merci ! Votre message a été envoyé avec succès. Nous vous recontacterons rapidement.');
        form.reset();
      } else {
        alert('Une erreur est survenue. Veuillez réessayer.');
      }
    } catch (error) {
      alert('Une erreur est survenue. Veuillez réessayer.');
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d5p5kp7hv1n1bb3qco6g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString()
      });

      if (response.ok) {
        alert('Merci pour votre inscription ! Vous recevrez bientôt nos actualités.');
        form.reset();
      } else {
        alert('Une erreur est survenue. Veuillez réessayer.');
      }
    } catch (error) {
      alert('Une erreur est survenue. Veuillez réessayer.');
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <h1 className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center">
              <i className="ri-code-s-slash-line text-white text-xl"></i>
            </div>
            <span className="text-xl font-semibold text-slate-900">DigiCreate</span>
          </h1>
          
          <nav className="hidden md:flex items-center gap-8">
            <a href="#services" className="text-slate-700 hover:text-slate-900 transition-colors text-sm">Services</a>
            <span className="text-slate-300">•</span>
            <a href="#portfolio" className="text-slate-700 hover:text-slate-900 transition-colors text-sm">Portfolio</a>
            <span className="text-slate-300">•</span>
            <a href="#processus" className="text-slate-700 hover:text-slate-900 transition-colors text-sm">Processus</a>
            <span className="text-slate-300">•</span>
            <a href="#temoignages" className="text-slate-700 hover:text-slate-900 transition-colors text-sm">Témoignages</a>
            <span className="text-slate-300">•</span>
            <a href="#faq" className="text-slate-700 hover:text-slate-900 transition-colors text-sm">FAQ</a>
            <span className="text-slate-300">•</span>
            <a href="#contact" className="text-slate-700 hover:text-slate-900 transition-colors text-sm">Contact</a>
          </nav>

          <div className="flex items-center gap-3">
            {/* Call Button in Header */}
            <a 
              href="tel:+33631012910"
              className="bg-teal-600 text-white w-12 h-12 rounded-full flex items-center justify-center hover:bg-teal-700 transition-all duration-300 hover:scale-110 shadow-lg cursor-pointer"
              aria-label="Appeler DigiCreate"
            >
              <i className="ri-phone-fill text-xl"></i>
            </a>
            
            <a 
              href="#contact"
              className="hidden md:flex bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-6 py-3 rounded-full text-sm font-medium hover:shadow-lg hover:scale-105 transition-all duration-300 items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              Démarrer mon projet
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="relative pt-40 pb-32 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50/50 via-white to-purple-50/50"></div>
        
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-teal-100/80 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-8">
              <i className="ri-sparkling-line"></i>
              Agence de création digitale Paris 2026
            </div>
            
            <h2 className="text-6xl md:text-7xl lg:text-8xl font-bold text-slate-900 mb-6 leading-tight">
              Studio de design graphique<br/>
              <span className="bg-gradient-to-r from-teal-600 to-purple-600 bg-clip-text text-transparent">& création digitale</span><br/>
              à Paris
            </h2>
            
            <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed mb-12">
              <strong>Agence de création digitale</strong> spécialisée en <strong>webdesign UX/UI</strong>, <strong>conception de logo sur mesure</strong> et <strong>création de site internet professionnel</strong>. 
              Notre <strong>studio de design graphique</strong> à Paris transforme votre <strong>identité visuelle de marque</strong> avec des solutions digitales innovantes : <strong>refonte de site web</strong>, <strong>boutique e-commerce Shopify</strong>, et <strong>supports de communication print & web</strong>.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <a 
                href="#contact"
                className="bg-slate-900 text-white px-8 py-4 rounded-full font-medium hover:bg-slate-800 transition-all duration-300 hover:scale-105 hover:shadow-xl flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                Demander un devis gratuit
                <i className="ri-arrow-right-line"></i>
              </a>
              <a 
                href="#portfolio"
                className="border-2 border-slate-900 text-slate-900 px-8 py-4 rounded-full font-medium hover:bg-slate-900 hover:text-white transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                Voir nos réalisations
                <i className="ri-eye-line"></i>
              </a>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <i className="ri-checkbox-circle-fill text-teal-600"></i>
                <span>150+ projets livrés</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-star-fill text-teal-600"></i>
                <span>Satisfaction 100%</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-customer-service-2-fill text-teal-600"></i>
                <span>Support 7j/7</span>
              </div>
            </div>
          </div>

          {/* Visual Gallery */}
          <div className="relative h-96 mt-20">
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-5xl">
              <div className="grid grid-cols-5 gap-4 items-center">
                <div className="transform -rotate-6 hover:rotate-0 transition-transform duration-500">
                  <div className="bg-white rounded-2xl shadow-2xl p-4 h-64 w-full overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=modern%20minimalist%20website%20mockup%20showcasing%20clean%20design%20with%20white%20background%2C%20elegant%20interface%2C%20professional%20layout%2C%20contemporary%20web%20design%20aesthetic%2C%20ample%20white%20space%2C%20sophisticated%20visual%20hierarchy&width=300&height=400&seq=hero1&orientation=portrait" 
                      alt="Project 1" 
                      className="w-full h-full object-cover object-top rounded-lg"
                    />
                  </div>
                </div>
                
                <div className="transform -rotate-3 hover:rotate-0 transition-transform duration-500">
                  <div className="bg-white rounded-2xl shadow-2xl p-4 h-72 w-full overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=sleek%20digital%20business%20card%20design%20with%20minimalist%20white%20background%2C%20modern%20typography%2C%20professional%20contact%20information%20layout%2C%20QR%20code%20integration%2C%20clean%20and%20contemporary%20style&width=300&height=400&seq=hero2&orientation=portrait" 
                      alt="Project 2" 
                      className="w-full h-full object-cover object-top rounded-lg"
                    />
                  </div>
                </div>
                
                <div className="transform scale-110 hover:scale-115 transition-transform duration-500 z-10">
                  <div className="bg-white rounded-2xl shadow-2xl p-4 h-80 w-full overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=premium%20responsive%20website%20design%20on%20multiple%20devices%20with%20clean%20white%20background%2C%20modern%20interface%20showcase%2C%20professional%20multi-device%20mockup%2C%20contemporary%20web%20application%20display%2C%20elegant%20presentation&width=300&height=400&seq=hero3&orientation=portrait" 
                      alt="Project 3" 
                      className="w-full h-full object-cover object-top rounded-lg"
                    />
                  </div>
                </div>
                
                <div className="transform rotate-3 hover:rotate-0 transition-transform duration-500">
                  <div className="bg-white rounded-2xl shadow-2xl p-4 h-72 w-full overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=innovative%20mobile-first%20website%20interface%20with%20white%20background%2C%20modern%20app%20design%2C%20clean%20user%20experience%20showcase%2C%20contemporary%20digital%20solution%2C%20streamlined%20navigation&width=300&height=400&seq=hero4&orientation=portrait" 
                      alt="Project 4" 
                      className="w-full h-full object-cover object-top rounded-lg"
                    />
                  </div>
                </div>
                
                <div className="transform rotate-6 hover:rotate-0 transition-transform duration-500">
                  <div className="bg-white rounded-2xl shadow-2xl p-4 h-64 w-full overflow-hidden">
                    <img 
                      src="https://readdy.ai/api/search-image?query=elegant%20portfolio%20website%20design%20with%20minimalist%20white%20background%2C%20artistic%20layout%2C%20professional%20creative%20showcase%2C%20modern%20gallery%20presentation%2C%20sophisticated%20design%20aesthetic&width=300&height=400&seq=hero5&orientation=portrait" 
                      alt="Project 5" 
                      className="w-full h-full object-cover object-top rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Services Section */}
      <section id="services" className="py-32 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <header className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-service-line"></i>
              Nos Services de Création Digitale
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
              Solutions digitales complètes<br/>pour votre réussite en ligne
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              De la <strong>création de site internet</strong> à la <strong>conception de logo</strong>, notre <strong>agence web Paris</strong> vous accompagne avec des <strong>solutions de création digitale</strong> sur-mesure. 
              Expertise en <strong>webdesign responsive</strong>, <strong>identité visuelle</strong> et <strong>développement web</strong> professionnel.
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <article 
                key={index}
                className={`bg-gradient-to-br ${service.gradient} rounded-3xl p-12 hover:shadow-2xl transition-all duration-500 hover:scale-105 ${
                  service.dark ? 'text-white' : 'text-slate-900'
                }`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className={`w-16 h-16 rounded-2xl ${
                    service.dark ? 'bg-white/10' : 'bg-white'
                  } flex items-center justify-center`}>
                    <i className={`${service.icon} text-3xl ${
                      service.dark ? 'text-white' : 'text-teal-600'
                    }`}></i>
                  </div>
                  <div className={`px-4 py-2 rounded-full text-sm font-medium ${
                    service.dark ? 'bg-white/20 backdrop-blur-sm' : 'bg-teal-100 text-teal-700'
                  }`}>
                    {service.price}
                  </div>
                </div>

                <h3 className="text-3xl font-bold mb-4">{service.title}</h3>
                <p className={`mb-8 ${service.dark ? 'text-white/80' : 'text-slate-600'}`}>
                  {service.description}
                </p>

                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <i className={`ri-checkbox-circle-line text-xl ${
                        service.dark ? 'text-teal-400' : 'text-teal-600'
                      }`}></i>
                      <span className={service.dark ? 'text-white/90' : 'text-slate-700'}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <a 
                  href="#contact"
                  className={`mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    service.dark 
                      ? 'bg-white text-slate-900 hover:bg-white/90' 
                      : 'bg-slate-900 text-white hover:bg-slate-800'
                  }`}
                >
                  En savoir plus
                  <i className="ri-arrow-right-line"></i>
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="processus" className="py-32 px-6 bg-gradient-to-br from-slate-50 to-stone-50">
        <div className="max-w-7xl mx-auto">
          <header className="text-center mb-20">
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-route-line"></i>
              Notre Méthodologie de Création Digitale
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
              Processus éprouvé pour<br/>votre projet digital
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-12">
              Notre méthodologie en 4 étapes garantit des résultats exceptionnels pour chaque <strong>projet de création de site internet</strong>, <strong>conception de logo</strong> et <strong>refonte de site web professionnel</strong>. 
              Une approche structurée alliant <strong>webdesign UX/UI</strong>, développement technique et <strong>charte graphique</strong> cohérente.
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <article key={index} className="relative">
                <div className="bg-white rounded-3xl p-8 hover:shadow-xl transition-all duration-500 h-full">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center mb-6`}>
                    <i className={`${step.icon} text-3xl text-white`}></i>
                  </div>
                  
                  <div className="text-6xl font-bold text-slate-100 mb-4">{step.number}</div>
                  
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">{step.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{step.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-32 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <header className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-gallery-line"></i>
              Portfolio Création Digitale
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
              Nos réalisations en<br/>création digitale & webdesign
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-12">
              Découvrez nos <strong>projets de création de site internet</strong>, <strong>conception de logo</strong>, <strong>identité visuelle de marque</strong> et <strong>refonte de site web</strong> réalisés pour des clients satisfaits à Paris et en France. 
              Expertise en <strong>webdesign responsive</strong>, <strong>boutique e-commerce</strong> et <strong>supports de communication</strong>.
            </p>

            <nav className="flex flex-wrap items-center justify-center gap-4">
              {['tous', 'Site Web', 'Carte Digitale'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className={`px-6 py-3 rounded-full font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeFilter === filter
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {filter === 'tous' ? 'Tous les projets' : filter}
                </button>
              ))}
            </nav>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPortfolio.map((item, index) => (
              <article key={index} className="group cursor-pointer">
                <div className="bg-slate-50 rounded-3xl overflow-hidden hover:shadow-2xl transition-all duration-500">
                  <div className="relative overflow-hidden aspect-[3/2]">
                    <img 
                      src={item.image}
                      alt={`${item.title} - Projet de ${item.category} réalisé par DigiCreate`}
                      title={`${item.title} - ${item.category}`}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                      <div className="text-white">
                        <div className="flex gap-2 mb-2">
                          {item.tags.map((tag, idx) => (
                            <span key={idx} className="text-xs bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-slate-600 text-sm">{item.category}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="temoignages" className="py-32 px-6 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto">
          <header className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-chat-quote-line"></i>
              Témoignages Clients
            </div>
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              Ils nous font confiance<br/>pour leur création digitale
            </h2>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              Plus de 150 clients satisfaits nous ont confié leur <strong className="text-white">création de site internet</strong>, <strong className="text-white">conception de logo</strong>, <strong className="text-white">refonte de site web</strong> et <strong className="text-white">identité visuelle de marque</strong>. 
              Notre <strong className="text-white">agence de création digitale à Paris</strong> garantit satisfaction et résultats.
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <article key={index} className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 hover:bg-white/10 transition-all duration-300">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400"></i>
                  ))}
                </div>
                <p className="text-white/90 mb-6 leading-relaxed">{testimonial.text}</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-purple-500 flex items-center justify-center">
                    <span className="text-white font-bold">{testimonial.author.charAt(0)}</span>
                  </div>
                  <div>
                    <div className="font-semibold text-white">{testimonial.author}</div>
                    <div className="text-sm text-white/60">{testimonial.company}</div>
                  </div>
                </div>
                <div className="mt-4 inline-block bg-teal-500/20 text-teal-300 px-3 py-1 rounded-full text-xs">
                  {testimonial.service}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-32 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <header className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-question-line"></i>
              Questions Fréquentes
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
              Vos questions sur la<br/>création digitale & webdesign
            </h2>
            <p className="text-lg text-slate-600">
              Tout ce que vous devez savoir sur nos services de <strong>création de site internet</strong>, <strong>conception de logo sur mesure</strong>, <strong>refonte de site web professionnel</strong>, <strong>identité visuelle de marque</strong> et <strong>optimisation SEO</strong>. 
              Notre <strong>agence web Paris</strong> répond à vos questions.
            </p>
          </header>

          <div className="space-y-6">
            {faqData.map((faq, index) => (
              <article key={index} className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-all duration-300">
                <h4 className="text-xl font-bold text-slate-900 mb-4 flex items-start gap-3">
                  <i className="ri-question-mark text-teal-600 mt-1"></i>
                  <a href={`#faq-${index}`} id={`faq-${index}`} className="hover:text-teal-600 transition-colors">
                    {faq.question}
                  </a>
                </h4>
                <p className="text-slate-600 leading-relaxed pl-8">{faq.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 px-6 bg-gradient-to-br from-slate-50 to-stone-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <header className="mb-12">
                <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <i className="ri-mail-line"></i>
                  Contact Agence Digitale Paris
                </div>
                <h2 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
                  Démarrons votre<br/>projet digital ensemble
                </h2>
                <p className="text-lg text-slate-600 mb-8">
                  Contactez notre <strong>agence de création digitale à Paris</strong> pour discuter de votre projet de <strong>création de site internet</strong>, <strong>conception de logo</strong>, <strong>refonte de site web</strong> ou <strong>identité visuelle de marque</strong>. 
                  Notre <strong>studio de design graphique</strong> vous accompagne dans votre transformation digitale. <strong>Devis gratuit sous 24h</strong> pour tous vos projets de <strong>webdesign</strong>, <strong>développement web responsive</strong> et <strong>création digitale</strong>.
                </p>
              </header>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Notre Agence</h3>
                    <p className="text-slate-600">Paris, France</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-purple-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Téléphone</h3>
                    <a href="tel:+33631012910" className="text-slate-600 hover:text-teal-600 transition-colors" rel="nofollow">
                      +33 6 31 01 29 10
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-orange-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                    <a href="mailto:contact@digicreate.fr" className="text-slate-600 hover:text-teal-600 transition-colors" rel="nofollow">
                      contact@digicreate.fr
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Horaires</h3>
                    <p className="text-slate-600">Lun - Ven : 9h - 18h</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-xl">
              <form onSubmit={handleContactSubmit} id="contact-form" data-readdy-form>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-900 mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm"
                      placeholder="Jean Dupont"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-900 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm"
                      placeholder="jean@exemple.fr"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-slate-900 mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm"
                      placeholder="+33 6 12 34 56 78"
                    />
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-slate-900 mb-2">
                      Service souhaité *
                    </label>
                    <select
                      id="service"
                      name="service"
                      required
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm cursor-pointer"
                    >
                      <option value="">Sélectionnez un service</option>
                      <option value="site-web">Création de Site Web</option>
                      <option value="carte-digitale">Carte de Visite Numérique</option>
                      <option value="seo">Optimisation SEO</option>
                      <option value="maintenance">Maintenance & Support</option>
                      <option value="autre">Autre</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-900 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all resize-none text-sm"
                      placeholder="Décrivez votre projet..."
                    ></textarea>
                    <p className="text-xs text-slate-500 mt-1">Maximum 500 caractères</p>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-8 py-4 rounded-full font-medium hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    Envoyer ma demande
                    <i className="ri-send-plane-line"></i>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-teal-600 to-cyan-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Restez informé des tendances en création digitale
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Recevez nos conseils en <strong className="text-white">création de site internet</strong>, <strong className="text-white">webdesign UX/UI</strong>, <strong className="text-white">conception de logo</strong>, <strong className="text-white">identité visuelle</strong> et <strong className="text-white">stratégie SEO</strong>. 
            Tendances <strong className="text-white">design graphique</strong> et <strong className="text-white">développement web</strong> 2025.
          </p>
          
          <form onSubmit={handleNewsletterSubmit} id="newsletter-form" data-readdy-form className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <input
              type="email"
              name="email"
              required
              placeholder="Votre email"
              className="flex-1 px-6 py-3 rounded-full outline-none text-slate-900 text-sm"
            />
            <button
              type="submit"
              className="bg-slate-900 text-white px-8 py-3 rounded-full font-medium hover:bg-slate-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              S'inscrire
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Quick Contact Section */}
          <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-3xl p-8 mb-12">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-white mb-3">Contactez notre agence digitale Paris</h3>
              <p className="text-white/70">Choisissez votre moyen de contact préféré pour votre projet de création digitale</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {/* WhatsApp */}
              <a
                href="https://wa.me/33631012910?text=Bonjour,%20je%20souhaite%20obtenir%20des%20informations%20sur%20vos%20services%20digitaux"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-2xl p-6 flex flex-col items-center gap-4 hover:scale-105 transition-all duration-300 shadow-xl cursor-pointer group"
              >
                <div className="w-16 h-16 bg-[#25D366] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <i className="ri-whatsapp-fill text-white text-3xl"></i>
                </div>
                <div className="text-center">
                  <div className="font-bold text-slate-900 mb-1">WhatsApp</div>
                  <div className="text-sm text-slate-600">Message instantané</div>
                </div>
              </a>

              {/* Phone */}
              <a
                href="tel:+33631012910"
                className="bg-white rounded-2xl p-6 flex flex-col items-center gap-4 hover:scale-105 transition-all duration-300 shadow-xl cursor-pointer group"
              >
                <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <i className="ri-phone-fill text-white text-3xl"></i>
                </div>
                <div className="text-center">
                  <div className="font-bold text-slate-900 mb-1">Téléphone</div>
                  <div className="text-sm text-slate-600">+33 6 31 01 29 10</div>
                </div>
              </a>

              {/* Email */}
              <a
                href="mailto:contact@digicreate.fr"
                className="bg-white rounded-2xl p-6 flex flex-col items-center gap-4 hover:scale-105 transition-all duration-300 shadow-xl cursor-pointer group"
              >
                <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <i className="ri-mail-fill text-white text-3xl"></i>
                </div>
                <div className="text-center">
                  <div className="font-bold text-slate-900 mb-1">Email</div>
                  <div className="text-sm text-slate-600">contact@digicreate.fr</div>
                </div>
              </a>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center">
                  <i className="ri-code-s-slash-line text-white text-xl"></i>
                </div>
                <span className="text-xl font-semibold">DigiCreate</span>
              </div>
              <p className="text-white/70 text-sm leading-relaxed">
                Votre <strong className="text-white">agence de création digitale</strong> et <strong className="text-white">studio de design graphique</strong> à Paris pour des <strong className="text-white">sites internet professionnels</strong>, <strong className="text-white">conception de logo</strong>, <strong className="text-white">identité visuelle de marque</strong> et <strong className="text-white">webdesign UX/UI</strong> innovants.
              </p>
            </div>

            <nav>
              <h3 className="font-semibold mb-4">Services Création Digitale</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#services" className="text-white/70 hover:text-white transition-colors">Création de Site Internet</a></li>
                <li><a href="#services" className="text-white/70 hover:text-white transition-colors">Conception de Logo & Identité Visuelle</a></li>
                <li><a href="#services" className="text-white/70 hover:text-white transition-colors">Refonte de Site Web Professionnel</a></li>
                <li><a href="#services" className="text-white/70 hover:text-white transition-colors">Boutique E-commerce Shopify</a></li>
              </ul>
            </nav>

            <nav>
              <h3 className="font-semibold mb-4">Entreprise</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#portfolio" className="text-white/70 hover:text-white transition-colors">Portfolio</a></li>
                <li><a href="#processus" className="text-white/70 hover:text-white transition-colors">Notre Processus</a></li>
                <li><a href="#temoignages" className="text-white/70 hover:text-white transition-colors">Témoignages</a></li>
                <li><a href="#faq" className="text-white/70 hover:text-white transition-colors">FAQ</a></li>
              </ul>
            </nav>

            <div>
              <h3 className="font-semibold mb-4">Suivez-nous</h3>
              <div className="flex gap-3">
                <a href="https://facebook.com" target="_blank" rel="nofollow noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-facebook-fill"></i>
                </a>
                <a href="https://twitter.com" target="_blank" rel="nofollow noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-twitter-x-fill"></i>
                </a>
                <a href="https://instagram.com" target="_blank" rel="nofollow noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-instagram-fill"></i>
                </a>
                <a href="https://linkedin.com" target="_blank" rel="nofollow noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/70">
            <p>© 2025 DigiCreate. Tous droits réservés.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="nofollow noopener noreferrer" className="hover:text-white transition-colors">
              Powered by Readdy
            </a>
          </div>
        </div>

        {/* Floating WhatsApp Button */}
        <a
          href="https://wa.me/33631012910?text=Bonjour,%20je%20souhaite%20obtenir%20des%20informations%20sur%20vos%20services%20digitaux"
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-24 right-6 z-50 w-16 h-16 bg-[#25D366] rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-all duration-300 cursor-pointer group animate-bounce hover:animate-none"
          aria-label="Contacter via WhatsApp"
        >
          <i className="ri-whatsapp-fill text-white text-3xl"></i>
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse"></span>
        </a>
      </footer>
    </div>
  );
}